import { SelectFieldOption } from '@resurs-internal/mui/components/SelectField/SelectFieldOption';
import * as React from 'react';
import { ChangeEvent, ReactElement, ReactNode, useState } from 'react';

import { useAppDispatch } from '../../hooks';
import useFieldFormatting, { FieldFormattingProps } from '../../hooks/useFieldFormatting';
import useFieldValidation, { FieldValidationProps, ValidationResultType } from '../../hooks/useFieldValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';

type InputFieldProps = {
    name: any;
    children: ReactNode;
    /**
     * Validation properties
     */
    validation?: FieldValidationProps;
    /**
     * Formatting properties
     */
    formatting?: FieldFormattingProps;
    /**
     * When the text is changed this function will be invoked after the value has been updated in the store.
     */
    postOnChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    preOnChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    radioFieldoOptions?: SelectFieldOption[];
    /**
     * Min and max values for numbers
     */
    min?: number;
    max?: number;
};

const InputField = (props: InputFieldProps) => {
    const dispatch = useAppDispatch();
    const { validate } = useFieldValidation();
    const { initialValue, formatValue, cleanValue } = useFieldFormatting(props.name, props.formatting);
    const [fieldValue, setFieldValue] = useState(formatValue(initialValue()));

    const onChangeProxy = (event: ChangeEvent<HTMLInputElement>) => {
        if (props?.preOnChange) {
            props.preOnChange(event);
        }

        // Clean the input value
        let value = cleanValue(event.target.value);

        //Apply logic if there is a numeric min or max value
        const isNumericField = props.min !== undefined || props.max !== undefined;

        if (isNumericField) {
            let numericValue = Number(value);

            if (props.min !== undefined) {
                numericValue = Math.max(props.min, numericValue);
            }
            if (props.max !== undefined) {
                numericValue = Math.min(props.max, numericValue);
            }

            value = isNaN(numericValue) ? '' : numericValue.toString();
        }

        //Find right option for the chosen value to get the label
        const option = props.radioFieldoOptions?.find((field) => field.id === value);

        (props.validation
            ? validate(props.name, value, props.validation)
            : Promise.resolve({ valid: true } as ValidationResultType)
        ).then((validationResult) =>
            dispatch(
                addOrUpdateFormField({
                    fieldName: props.name,
                    label: option?.label,
                    value,
                    hasError: !validationResult.valid,
                    errorMessage: validationResult.errorMessageKey,
                } as IFieldState),
            ),
        );

        // Set the corrected value
        setFieldValue(formatValue(value));
        if (props?.postOnChange) {
            props.postOnChange(event);
        }
    };

    return React.Children.map(props.children, (child) => {
        if (!React.isValidElement(child)) {
            return <>Element {child} cannot be cloned</>;
        }

        return React.cloneElement(child as ReactElement, { value: fieldValue, onChange: onChangeProxy } as any, null);
    });
};

export default InputField;
